import React, { useState } from "react";
import { Camera, CheckCircle2, XCircle, Wrench } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { useTranslation } from "react-i18next";

function TestingCaseDetail({ testCase }) {
    const { t } = useTranslation();
    const [showScreenshot, setShowScreenshot] = useState(false);
    const steps = testCase.steps || [];
    const assertions = testCase.assertions || [];

    return (
        <div className="px-4 pb-3 pl-12 space-y-3">
            {steps.length > 0 && (
                <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-1">
                        {t("testing.steps") || "Steps"}
                    </h4>
                    <div className="space-y-1">
                        {steps.map((step, i) => (
                            <div key={i} className="text-xs text-gray-600 flex items-center gap-1">
                                <span className="text-gray-400">{i + 1}.</span>
                                <span>
                                    {step.action}{" "}
                                    {step.page || step.field || step.target || ""}
                                    {step.value ? ` = "${step.value}"` : ""}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {assertions.length > 0 && (
                <div>
                    <h4 className="text-xs font-medium text-gray-500 mb-1">
                        {t("testing.assertions") || "Assertions"}
                    </h4>
                    <div className="space-y-1">
                        {assertions.map((a, i) => (
                            <div key={i} className="text-xs text-gray-600 flex items-center gap-1">
                                <span className="text-gray-400">&bull;</span>
                                <span>{a.description || a.type}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {testCase.screenshot && (
                <div>
                    <button
                        onClick={() => setShowScreenshot(!showScreenshot)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                        <Camera className="w-3 h-3" />
                        {t("testing.viewScreenshot") || "View Screenshot"}
                    </button>
                    {showScreenshot && (
                        <img
                            src={testCase.screenshot}
                            alt="Failure screenshot"
                            className="mt-2 rounded border border-gray-200 max-w-full max-h-64 object-contain"
                        />
                    )}
                </div>
            )}
            {testCase.lastRunStatus === "failed" && testCase.healingAttempt?.suggestion && (
                <HealingSuggestion suggestion={testCase.healingAttempt.suggestion} />
            )}
        </div>
    );
}

function HealingSuggestion({ suggestion }) {
    const { t } = useTranslation();
    return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex items-center gap-1 text-xs font-medium text-yellow-800 mb-1">
                <Wrench className="w-3 h-3" />
                {t("testing.aiSuggestedFix") || "AI Suggested Fix"}
            </div>
            <p className="text-xs text-yellow-700 mb-2">{suggestion.description}</p>
            {suggestion.oldCode && (
                <pre className="text-xs bg-white border rounded p-2 overflow-x-auto">
                    <div className="text-red-600">- {suggestion.oldCode}</div>
                    <div className="text-green-600">+ {suggestion.newCode}</div>
                </pre>
            )}
            <div className="flex items-center gap-2 mt-2">
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {t("testing.applyFix") || "Apply Fix & Rerun"}
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7">
                    {t("testing.dismiss") || "Dismiss"}
                </Button>
            </div>
        </div>
    );
}

export default TestingCaseDetail;
